import { format } from "date-fns";
import { useMemo } from "react";

import { type ResourceConfig, ResourcePage } from "@/components/resource-page";
import type { Translate } from "@/constants/i18n";
import { useTranslation } from "@/store/locale";

interface Change {
  field: string;
  old_value: string | null;
  new_value: string | null;
}

interface LogEntry {
  id: number;
  member_id: number;
  member_number: string;
  change_data: Change[];
  created_at: string;
  created_by: string;
  club_location: string;
}

const dash = (value: string | null) => value ?? "—";

/** One box per change, stacked, so the three change columns line up row for row. */
function Stack({ entry, pick }: { entry: LogEntry; pick: (change: Change) => string | null }) {
  return (
    <span className="grid gap-2">
      {entry.change_data.map((change, index) => (
        <span
          key={`${change.field}-${index}`}
          className="border-line bg-surface-sunken text-fg text-callout block truncate rounded-lg border px-3 py-2"
        >
          {dash(pick(change))}
        </span>
      ))}
    </span>
  );
}

/** One change to a line, the way `Stack` draws them and the way the server's
 *  own export writes them: joined on one line they line up only until a value
 *  is long, and an amendment reason is a sentence. */
const stacked = (entry: LogEntry, pick: (change: Change) => string | null) =>
  entry.change_data.map((change) => dash(pick(change))).join("\n");

export const memberActivityLogConfig = (t: Translate): ResourceConfig<LogEntry> => ({
  title: t("page.memberActivityLog"),
  singular: t("log.entry"),
  endpoint: "/member-logs",
  backTo: "/members",
  readOnly: true,
  // No row menu at all. `readOnly` already took Edit and Delete, which left a
  // menu holding one item — View — and a whole column pinned to the right edge
  // to carry it. The row is the entry: every field the detail panel would show
  // is already across it, so the menu offered a second look at what was on
  // screen. The employee log has read this way all along; this is the member
  // log catching up with it.
  noView: true,
  exportable: true,
  // The file is written by the server, not from the rows in the browser. This
  // list is `infinite` and so has no total, which means the export button's
  // "the screen already holds every match" shortcut can never be true here:
  // without a route behind it the button wrote whichever page happened to be
  // loaded and said nothing about the rest.
  // No `permission`: unlike `importable`, the field is declared but never read,
  // and the codename that matters is enforced on `POST /member-logs/export` —
  // and on this page, which cannot be reached without it either way.
  exportServer: {},
  // Every change ever made: it scrolls back through time, and nobody counts it.
  infinite: true,
  searchPlaceholder: t("log.memberNumberSearch"),
  columns: [
    {
      key: "member_number",
      label: t("log.memberAccountNumber"),
      render: (r) => r.member_number,
    },
    {
      key: "field",
      label: t("log.modification"),
      className: "min-w-56",
      render: (r) => <Stack entry={r} pick={(c) => c.field} />,
      csv: (r) => stacked(r, (c) => c.field),
    },
    {
      key: "from",
      label: t("log.from"),
      className: "min-w-56",
      render: (r) => <Stack entry={r} pick={(c) => c.old_value} />,
      csv: (r) => stacked(r, (c) => c.old_value),
    },
    {
      key: "to",
      label: t("log.to"),
      className: "min-w-56",
      render: (r) => <Stack entry={r} pick={(c) => c.new_value} />,
      csv: (r) => stacked(r, (c) => c.new_value),
    },
    {
      key: "created_at",
      label: t("log.modifiedAt"),
      render: (r) => format(new Date(r.created_at), "MMM d, yyyy, h:mm a"),
    },
    { key: "created_by", label: t("log.modifiedBy"), render: (r) => r.created_by },
    { key: "club_location", label: t("log.clubLocation"), render: (r) => r.club_location },
  ],
  filters: [
    { name: "field", label: t("log.modification"), type: "text" },
    { name: "created_by", label: t("log.modifiedBy"), type: "text" },
    { name: "club_location", label: t("log.clubLocation"), type: "text" },
    { name: "date_from", label: t("log.fromDate"), type: "date" },
    { name: "date_to", label: t("log.toDate"), type: "date" },
  ],
  fields: [],
  toForm: () => ({}),
});

function MemberActivityLogPage() {
  const t = useTranslation();
  const config = useMemo(() => memberActivityLogConfig(t), [t]);
  return <ResourcePage config={config} />;
}

export default MemberActivityLogPage;
